import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import MathSymbolPicker from '../../../../../globalComponents/MathSymbolPicker';
import './QuestionPOPup.css';

// Toast notification function
const toast = {
  success: (message) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast custom-toast-success';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  },
  error: (message) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast custom-toast-error';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  },
};

const QuestionPopup = ({ onClose, questionData, loading, refreshData }) => {
  const [formData, setFormData] = useState(questionData?.data ?? {});
  const [options, setOptions] = useState(questionData?.data?.options ?? []);
  const [saving, setSaving] = useState(false);


  useEffect(() => {
    if (questionData?.data) {
      setFormData({
        ...questionData.data,
        image: questionData.data.image
          ? `${axiosInstance.defaults.baseURL}${questionData.data.image}`
          : "",
      });

      const updatedOptions = (questionData.data.options || []).map((option) => ({
        ...option,
        preview: option.image ? `${axiosInstance.defaults.baseURL}${option.image}` : null,
        image: null, // This will only hold File when updated
      }));

      setOptions(updatedOptions);
    }
  }, [questionData]);




  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'question_type' ? value.toLowerCase() : value
    });
  };
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({ ...formData, image: file }); // Store as a File object for upload
      };
      reader.readAsDataURL(file);
    }
  };

  const saveQuestion = async () => {
    try {
      const questionId = formData.id;
      const formDataToSend = new FormData();

      formDataToSend.append('text', formData.text);
      formDataToSend.append('is_randomized', formData.is_randomized);
      formDataToSend.append('marks', formData.marks);
      formDataToSend.append('question_type', formData.question_type?.toLowerCase());

      if (formData.image instanceof File) {
        formDataToSend.append('image', formData.image); // Append image file
      }

      const response = await axiosInstance.put(
        `assessments/questions/${questionId}/`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status !== 200) {
        throw new Error('Failed to save question.');
      }
    } catch (error) {
    }
  };

  const handleSave = async () => {
    setSaving(true);  // Start the saving process

    try {
      await saveQuestion();

      // Show success toast
      toast.success('Question saved successfully!');
      onClose();  // Close the popup
      refreshData(); 
    } catch (error) {
      // Show error toast
      toast.error('Failed to save question. Please try again.');
    } finally {
      setSaving(false);  // Stop the saving process (loader off)
    }
  };

  // Use React Portal to render modal outside of current DOM hierarchy
  return ReactDOM.createPortal(
    <div className="edit-question-modal-overlay" onClick={onClose}>
      <div className="edit-question-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="edit-question-modal-header">
          <h2>✏️ Edit Question</h2>
          <button className="edit-question-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <div className="edit-question-loading">
            <div className="edit-question-spinner"></div>
            <p>Loading question...</p>
          </div>
        ) : (
          <>
            {/* Modal Body */}
            <div className="edit-question-modal-body">
              <form className="edit-question-form">
                {/* Question Text */}
                <div className="edit-question-form-group">
                  <label className="edit-question-label">
                    <span className="edit-question-label-text">Question Text <span className="required">*</span></span>
                    <textarea
                      name="text"
                      value={formData.text || ''}
                      onChange={handleInputChange}
                      rows="4"
                      className="edit-question-textarea"
                      placeholder="Enter question text..."
                    />
                  </label>
                </div>

                {/* Image and Question Type Row */}
                <div className="edit-question-form-row">
                  {/* Image Upload */}
                  <div className="edit-question-form-group edit-question-image-group">
                    <label className="edit-question-label">
                      <span className="edit-question-label-text">Question Image</span>
                      {formData.image ? (
                        <div className="edit-question-image-preview">
                          <img
                            src={
                              formData.image instanceof File
                                ? URL.createObjectURL(formData.image)
                                : formData.image || process.env.REACT_APP_PLACEHOLDER_IMAGE
                            }
                            alt="Question"
                            className="edit-question-preview-img"
                            onError={(e) => (e.target.style.display = "none")}
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById("edit-question-image-upload").click()}
                            className="edit-question-update-img-btn"
                          >
                            📷 Update Image
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => document.getElementById("edit-question-image-upload").click()}
                          className="edit-question-upload-img-btn"
                        >
                          📷 Upload Image
                        </button>
                      )}
                      <input
                        type="file"
                        id="edit-question-image-upload"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleImageUpload}
                      />
                    </label>
                  </div>

                  {/* Question Type */}
                  <div className="edit-question-form-group">
                    <label className="edit-question-label">
                      <span className="edit-question-label-text">Question Type <span className="required">*</span></span>
                      <select
                        name="question_type"
                        value={formData.question_type || ''}
                        onChange={handleInputChange}
                        className="edit-question-select"
                      >
                        <option value="" disabled>
                          Select Question Type
                        </option>
                        <option value="mcsr">Multiple Choice - Single Response</option>
                        <option value="eitheror">Either-Or</option>
                        <option value="subjective">Subjective</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Randomize and Marks Row */}
                <div className="edit-question-form-row">
                  {/* Randomize Checkbox */}
                  <div className="edit-question-form-group">
                    <label className="edit-question-checkbox-label">
                      <input
                        type="checkbox"
                        name="is_randomized"
                        checked={formData.is_randomized || false}
                        onChange={(e) =>
                          setFormData({ ...formData, is_randomized: e.target.checked })
                        }
                        className="edit-question-checkbox"
                      />
                      <span className="edit-question-checkbox-text">🔀 Randomize Options</span>
                    </label>
                  </div>

                  {/* Marks */}
                  <div className="edit-question-form-group">
                    <label className="edit-question-label">
                      <span className="edit-question-label-text">Marks <span className="required">*</span></span>
                      <input
                        type="number"
                        name="marks"
                        value={formData.marks || ''}
                        onChange={handleInputChange}
                        className="edit-question-input"
                        placeholder="Enter marks"
                        min="0"
                        step="0.5"
                      />
                    </label>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="edit-question-modal-footer">
              <button 
                type="button" 
                onClick={onClose} 
                className="edit-question-cancel-btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="edit-question-save-btn"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="edit-question-btn-spinner"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    💾 Save Changes
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default QuestionPopup;
